/**
 * WM-2: Midway OAuth 2.0 / OIDC authentication for MailHub WebMail.
 *
 * Flow:
 * 1. App loads → check for id_token in URL fragment or session
 * 2. No token → redirect to Midway SSO endpoint (implicit flow, response_type=id_token)
 * 3. Midway authenticates → redirects back with id_token in fragment
 * 4. App captures token → stores in-memory → attaches as Bearer header
 *
 * The Midway Client ID is the domain:port of the application.
 */

const MIDWAY_SSO_URL = 'https://midway-auth.amazon.com/SSO/redirect';
const CLIENT_ID = 'https://mailhub.gakhar.people.amazon.dev:443';
const REDIRECT_URI = 'https://mailhub.gakhar.people.amazon.dev';
const SCOPES = 'openid';

/** In-memory token storage (never persisted to disk/localStorage) */
let currentToken: string | null = null;
let tokenExpiry: number | null = null;

export interface MidwayUser {
  alias: string;
  email?: string;
  sub?: string;
}

/**
 * Get the current valid token, or null if not authenticated.
 */
export function getToken(): string | null {
  if (currentToken && tokenExpiry && Date.now() < tokenExpiry) {
    return currentToken;
  }
  // Token expired
  currentToken = null;
  tokenExpiry = null;
  return null;
}

/**
 * Parse a JWT payload without verification (client-side only).
 */
function parseJwt(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(jsonPayload);
}

/**
 * Extract the user info from the current token.
 */
export function getCurrentUser(): MidwayUser | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = parseJwt(token);
    return {
      alias: (payload.sub as string) || 'unknown',
      email: payload.email as string | undefined,
      sub: payload.sub as string | undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Try to capture a token from the URL (fragment or query params).
 * Midway may return the id_token in the hash fragment or as a query param.
 * Returns true if a token was captured.
 */
export function captureTokenFromUrl(): boolean {
  // Check URL fragment (implicit flow: #id_token=...)
  const fragment = window.location.hash.substring(1);
  const fragmentParams = new URLSearchParams(fragment);
  let idToken = fragmentParams.get('id_token');

  // Also check query params (some Midway configs use query string)
  if (!idToken) {
    const queryParams = new URLSearchParams(window.location.search);
    idToken = queryParams.get('id_token');
  }

  if (idToken) {
    try {
      const payload = parseJwt(idToken);
      const exp = (payload.exp as number) * 1000; // Convert to milliseconds
      currentToken = idToken;
      tokenExpiry = exp;

      // Clean the URL (remove token from address bar)
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    } catch (e) {
      console.error('Failed to parse Midway token', e);
      return false;
    }
  }

  return false;
}

/**
 * Attempt a silent token fetch (credentialed fetch to Midway SSO endpoint).
 * This works if the user already has an active Midway session (cookies).
 * Returns true if token was obtained silently.
 */
export async function trySilentAuth(): Promise<boolean> {
  try {
    const nonce = crypto.randomUUID();
    const url = `https://midway-auth.amazon.com/SSO?response_type=id_token&client_id=${encodeURIComponent(CLIENT_ID)}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${SCOPES}&nonce=${nonce}`;

    const response = await fetch(url, {
      credentials: 'include',
      redirect: 'manual',
    });

    if (response.ok) {
      const text = await response.text();
      // Token might be in the response body or we need to parse it
      if (text && text.includes('.')) {
        // Looks like a JWT
        const payload = parseJwt(text);
        if (payload.exp) {
          currentToken = text;
          tokenExpiry = (payload.exp as number) * 1000;
          return true;
        }
      }
    }
  } catch {
    // Silent auth failed — user needs interactive login
  }
  return false;
}

/**
 * Redirect to Midway login page (interactive flow).
 * This navigates away from the app — the user will be redirected back after login.
 */
export function redirectToMidway(): void {
  const nonce = crypto.randomUUID();
  const state = crypto.randomUUID();

  // Save current route for restoration after redirect
  sessionStorage.setItem('mailhub_auth_state', state);
  sessionStorage.setItem('mailhub_pre_auth_route', window.location.pathname);

  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    nonce,
    state,
  });

  window.location.href = `${MIDWAY_SSO_URL}?${params.toString()}`;
}

/**
 * Clear the in-memory token and redirect to Midway logout.
 */
export function logout(): void {
  currentToken = null;
  tokenExpiry = null;
  // Redirect to Midway logout
  window.location.href = 'https://midway-auth.amazon.com/logout';
}

/**
 * Check if user is currently authenticated with a valid (non-expired) token.
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}
